import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import GlossaryHint from './GlossaryHint';
import { StatusPill } from './ui';
import { FACT_SOURCE, IMMUTABLE_KIND, REPORT_STATUS, SEVERITY, formatDate, formatDateTime, gradeTone } from '../lib/format';
import { errorMessage, openProtectedFile } from '../lib/http';
import { BTN_SOFT, BTN_SOFT_STYLE, CARD, CARD_STYLE } from '../lib/ui';
import type { RatingReport, RemediationItem, SourcedFact } from '../types/workspace';

const TONE_COLOR = { green: '#059669', amber: '#d97706', rose: '#e11d48', slate: '#64748b', pink: '#d6006e', blue: '#2563eb' };

export function GradeDial({ grade, score, size = 116 }: { grade: string; score: number; size?: number }) {
  const r = 46;
  const c = 2 * Math.PI * r;
  const color = TONE_COLOR[gradeTone(score)];
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg viewBox="0 0 110 110" className="h-full w-full -rotate-90">
        <circle cx="55" cy="55" r={r} fill="none" stroke="#fce7f3" strokeWidth="9" />
        <circle cx="55" cy="55" r={r} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round" strokeDasharray={`${(score / 100) * c} ${c}`} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-black tracking-tight text-[#6b0030]">{grade}</span>
        <span className="text-xs font-bold text-[#c084a0]">{score}/100</span>
      </div>
    </div>
  );
}

const SEVERITY_BAR: Record<string, string> = { HIGH: '#e11d48', MEDIUM: '#d97706', LOW: '#94a3b8' };

/** Source dates arrive as YYYY-MM-DD strings; fall back to the raw text if unparseable. */
function formatSourceDate(value: string): string {
  return Number.isNaN(new Date(value).getTime()) ? value : formatDate(value);
}

function FindingCard({ item }: { item: RemediationItem }) {
  const sev = SEVERITY[item.severity];
  const changeable = item.changeable !== false;
  const immutable = !changeable ? IMMUTABLE_KIND[String(item.immutableKind || 'OTHER').toUpperCase()] || IMMUTABLE_KIND.OTHER : null;
  const exact = (item.exactChange || (!changeable ? null : item.detail) || '').trim();
  const docName = item.documentTitle || item.documentCode;

  return (
    <li
      className="rounded-xl px-3.5 py-3"
      style={{
        background: changeable ? '#fff' : '#fffbeb',
        border: `1px solid ${changeable ? '#fce7f3' : '#fcd34d'}`,
        borderLeft: `4px solid ${changeable ? SEVERITY_BAR[item.severity] || '#94a3b8' : '#d97706'}`,
      }}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-[#6b0030]">{item.title}</p>
          {docName && (
            <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-[#d6006e]">
              Document · {docName}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <StatusPill label={sev?.label || item.severity} tone={sev?.tone || 'slate'} />
          {immutable && <StatusPill label={`Cannot change · ${immutable.label}`} tone="amber" />}
        </div>
      </div>

      {changeable && exact && (
        <div className="mt-2.5 rounded-lg px-3 py-2" style={{ background: '#fff5f8', border: '1px solid #fce7f3' }}>
          <p className="text-xs font-bold uppercase tracking-wider text-[#d6006e]">
            {item.severity === 'HIGH' ? 'AI mitigation' : 'Exact change required'}
          </p>
          <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-[#4a1a30]">{exact}</p>
        </div>
      )}

      {immutable && (
        <div className="mt-2.5 rounded-lg px-3 py-2" style={{ background: '#fff7ed', border: '1px solid #fdba74' }}>
          <p className="text-xs font-bold uppercase tracking-wider text-[#b45309]">Cannot be changed</p>
          <p className="mt-1 text-sm leading-relaxed text-[#92400e]">{immutable.hint}</p>
        </div>
      )}

      {item.detail && (exact !== item.detail.trim() || !changeable) && (
        <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-[#4a1a30]">{item.detail}</p>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#9a6b82]">
        {changeable && docName && (
          <Link to="/documents" className="font-semibold text-[#d6006e] hover:underline">
            Update {docName} in documents →
          </Link>
        )}
        <Link to="/messages" className="font-semibold text-[#d6006e] hover:underline" title="Open Messages and quote this finding title">
          Ask your analyst about this finding
        </Link>
      </div>
    </li>
  );
}

function ReportSection({ title, children, open = true }: { title: string; children: ReactNode; open?: boolean }) {
  return (
    <details className="group border-t border-[#fce7f3] bg-white" open={open}>
      <summary className="flex cursor-pointer list-none items-center justify-between px-5 py-4 text-xs font-black uppercase tracking-[0.14em] text-[#6b0030] hover:bg-[#fffafc]">
        {title}<span className="text-lg text-[#d6006e] group-open:rotate-45" aria-hidden>+</span>
      </summary>
      <div className="px-5 pb-5">{children}</div>
    </details>
  );
}

/** Strengths and risks share one layout so source pills, dates and references appear on both. */
function FactList({ facts, dot }: { facts: SourcedFact[]; dot: 'strength' | 'risk' }) {
  return (
    <ul className="space-y-3">
      {facts.map((fact, i) => (
        <li key={i} className="text-sm text-[#4a1a30]">
          <div className="flex gap-2">
            <span className={dot === 'strength' ? 'text-emerald-600' : 'text-rose-600'} aria-hidden>●</span>
            <span>{fact.text}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 pl-5">
            <StatusPill label={fact.source} tone={FACT_SOURCE[fact.source]?.tone || 'slate'} />
            {fact.sourceDate && <span className="text-xs text-[#9a6b82]">As of {formatSourceDate(fact.sourceDate)}</span>}
            {fact.reference && <span className="text-xs text-[#9a6b82]">Ref: {fact.reference}</span>}
          </div>
          {fact.whatThisMeans && (
            <p className="mt-1 pl-5 text-xs text-[#9a6b82]"><strong>What this means:</strong> {fact.whatThisMeans}</p>
          )}
        </li>
      ))}
    </ul>
  );
}

function MeterBar({ label, value, caption }: { label: string; value: number | null; caption: string }) {
  const pct = value == null ? 0 : Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div>
      <div className="flex items-center justify-between gap-2 text-xs font-semibold text-[#6b0030]">
        <span>{label}</span>
        <span className="text-[#9a6b82]">{caption}</span>
      </div>
      <div className="mt-1 h-2 overflow-hidden rounded-full bg-[#fce7f3]" role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
        <div className="h-full bg-[#d6006e]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function RatingPdfButton({ reportId, label = 'Download rating PDF' }: { reportId: string; label?: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      await openProtectedFile(`/customer/reports/${reportId}/pdf`);
    } catch (err) {
      setError(errorMessage(err, 'Could not open the rating PDF.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="inline-flex flex-col items-start gap-1">
      <button type="button" onClick={() => void download()} disabled={busy} className={BTN_SOFT} style={BTN_SOFT_STYLE}>
        {busy ? 'Preparing PDF…' : label}
      </button>
      {error && <span role="alert" className="text-xs font-semibold text-[#be123c]">{error}</span>}
    </span>
  );
}

export default function RatingReportView({
  report,
  defaultOpen = false,
  nextAction,
}: {
  report: RatingReport;
  defaultOpen?: boolean;
  nextAction?: string;
}) {
  const strengths: SourcedFact[] = report.strengthsDetailed?.length ? report.strengthsDetailed : (report.strengths || []).map((text) => ({ text, source: 'Not verified' }));
  const risks: SourcedFact[] = report.risksDetailed?.length ? report.risksDetailed : (report.risks || []).map((text) => ({ text, source: 'Not verified' }));
  const findings = report.remediations || [];
  const status = report.status ? REPORT_STATUS[report.status] : null;
  const actionable = findings.filter((f) => f.changeable !== false);
  const awareness = findings.filter((f) => f.changeable === false);
  const breakdown = report.scoreBreakdown;
  const preparedBy = report.preparedByLabel || 'Micus Assessment Team';
  const publisherName = report.publishedBy?.name || null;
  const publishedScore = breakdown?.publishedScore ?? report.score;

  return (
    <>
      <article className={`${CARD} overflow-hidden`} style={CARD_STYLE}>
        <div className="flex flex-wrap items-center gap-5 p-5" style={{ background: 'linear-gradient(135deg, #fff5f8 0%, #ffffff 70%)' }}>
          <GradeDial grade={report.grade} score={report.score} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill label={`Version ${report.version}`} tone="pink" />
              {status && <StatusPill label={status.label} tone={status.tone} />}
              {report.outlook && <StatusPill label={`${report.outlook} outlook`} tone={report.outlook.toLowerCase().startsWith('neg') ? 'rose' : 'green'} />}
              <span className="text-xs text-[#9a6b82]">{formatDateTime(report.publishedAt || report.createdAt)}</span>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-[#4a1a30]">{report.summary}</p>
            {report.summaryFact && (
              <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                <StatusPill label={report.summaryFact.source} tone={FACT_SOURCE[report.summaryFact.source]?.tone || 'slate'} />
                {report.summaryFact.sourceDate && <span className="text-xs text-[#9a6b82]">As of {formatSourceDate(report.summaryFact.sourceDate)}</span>}
                {report.summaryFact.whatThisMeans && (
                  <p className="basis-full text-xs text-[#9a6b82]"><strong>What this means:</strong> {report.summaryFact.whatThisMeans}</p>
                )}
              </div>
            )}
            <p className="mt-2 text-xs text-[#9a6b82]">
              Prepared by {preparedBy}
              {publisherName && <> · Accountable analyst: <span className="font-semibold text-[#6b0030]">{publisherName}</span></>}
            </p>
            <div className="mt-3">
              <RatingPdfButton reportId={report.id} />
            </div>
          </div>
        </div>

        <ReportSection title="What this rating means">
          <p className="text-sm leading-relaxed text-[#4a1a30]">{breakdown?.gradeScaleExplanation || 'This is a point-in-time assessment based on the evidence available. It is not a guarantee of funding or future performance.'}</p>
          <p className="mt-2 text-xs text-[#9a6b82]">Published to your private workspace. Funding partners only see it after Ready approval and controlled sharing.</p>
        </ReportSection>

        {breakdown && (
          <ReportSection title="Score breakdown">
            <div className="mb-4 rounded-xl border border-[#fce7f3] bg-[#fffafc] p-3">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <p className="text-sm text-[#6b0030]">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#9a6b82]">Published score</span>
                  <br />
                  <span className="text-xl font-black">{publishedScore ?? '—'}</span><span className="text-xs text-[#9a6b82]">/100</span>
                </p>
                <p className="text-sm text-[#6b0030]">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#9a6b82]">Points earned</span>
                  <br />
                  <span className="text-xl font-black">{breakdown.totalPointsEarned ?? '—'}</span><span className="text-xs text-[#9a6b82]">/{breakdown.totalPointsAvailable} points</span>
                </p>
              </div>
              {breakdown.reconciliationNote && <p className="mt-2 text-xs leading-relaxed text-[#6b3a52]">{breakdown.reconciliationNote}</p>}
            </div>

            <div className="space-y-3">
              {breakdown.categories.map((category) => {
                const creditPct = category.creditContribution != null && category.pointsAvailable > 0 ? (category.creditContribution / category.pointsAvailable) * 100 : null;
                return (
                  <div key={category.key} className="rounded-xl border border-[#fce7f3] p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-bold text-[#6b0030]">{category.label} <span className="font-normal text-[#9a6b82]">· {category.weight}% weight</span></p>
                      <div className="flex flex-wrap gap-1.5">
                        <StatusPill label={`${category.pointsEarned ?? '—'} / ${category.pointsAvailable} points`} tone="pink" />
                        {category.verdict && <StatusPill label={category.verdict} tone={category.verdict === 'INSUFFICIENT' ? 'amber' : 'slate'} />}
                      </div>
                    </div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <MeterBar
                        label="Evidence completeness"
                        value={category.completenessScore}
                        caption={category.completenessScore == null ? 'Not assessed' : `${category.completenessScore}%`}
                      />
                      <MeterBar
                        label="Credit contribution"
                        value={creditPct}
                        caption={category.creditContribution == null ? 'Not scored' : `${category.creditContribution} of ${category.pointsAvailable} points`}
                      />
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-[#6b3a52]">{category.summary || category.measureExplanation}</p>
                    {category.verdict === 'INSUFFICIENT' && <p className="mt-1 text-xs font-semibold text-amber-700">This measures evidence completeness, not the financial strength of the business.</p>}
                    {category.deductions.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-bold uppercase tracking-wider text-[#9a6b82]">Deductions</p>
                        <ul className="mt-1 space-y-0.5">
                          {category.deductions.map((d, i) => (
                            <li key={i} className="text-xs leading-relaxed text-[#be123c]">
                              − {d}
                              <span className="mt-0.5 block pl-3 text-xs text-[#9a6b82]"><strong>What this means:</strong> {d}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </ReportSection>
        )}

        {strengths.length > 0 && <ReportSection title="Strengths"><FactList facts={strengths} dot="strength" /></ReportSection>}
        {risks.length > 0 && <ReportSection title="Key risks"><FactList facts={risks} dot="risk" /></ReportSection>}

        {findings.length > 0 && (
          <ReportSection title="Document findings">
            <p className="mb-3 text-xs text-[#9a6b82]">{actionable.length} actionable · {awareness.length} awareness-only</p>
            <ul className="space-y-2.5">{[...actionable, ...awareness].map((item) => <FindingCard key={item.id} item={item} />)}</ul>
          </ReportSection>
        )}

        <ReportSection title="Recommended actions">
          <p className="text-sm text-[#4a1a30]">{nextAction || (actionable.length ? 'Resolve the highest-priority document findings, then submit the updated pack for review.' : 'Review the rating and continue to the Ready pack when you are satisfied.')}</p>
          <Link to="/documents" className="mt-3 inline-flex rounded-xl bg-[#d6006e] px-4 py-2 text-xs font-bold text-white">Open documents →</Link>
        </ReportSection>

        <ReportSection title="Methodology and sources" open={defaultOpen}>
          <p className="text-sm text-[#4a1a30]">{breakdown?.methodologyVersion || report.methodologyVersion || 'Micus rating methodology'}</p>
          <p className="mt-1 text-xs text-[#9a6b82]">Score type: {breakdown?.scoreKind || 'analyst-approved'} · Evidence sources are labelled on each fact. <GlossaryHint meaning="The methodology combines verified evidence, category completeness and analyst review into the published grade." /></p>
          <p className="mt-2 text-xs font-semibold leading-relaxed text-[#6b0030]">Authoritative sourced facts are Strengths and Key risks. Narrative Markdown is explanatory only and is not separately source-labeled.</p>
          {report.validUntil && <p className="mt-2 text-xs text-[#6b3a52]"><strong>Valid / review until:</strong> {formatDate(report.validUntil)}</p>}
          {report.limitations && <p className="mt-1 text-xs leading-relaxed text-[#6b3a52]"><strong>Limitations:</strong> {report.limitations}</p>}
        </ReportSection>

        <ReportSection title="Review history" open={false}>
          <p className="text-sm text-[#4a1a30]">
            Version {report.version} · Published {formatDateTime(report.publishedAt || report.createdAt)} · Prepared by {preparedBy}
            {publisherName ? ` · Accountable analyst ${publisherName}` : ''}.
          </p>
        </ReportSection>
      </article>

      {nextAction && (
        <div className="sticky bottom-0 z-20 -mx-1 border-t border-[#f9c0d8] bg-white/95 px-4 py-3 backdrop-blur sm:hidden" role="status">
          <p className="text-xs font-bold text-[#6b0030]">Next action: {nextAction}</p>
        </div>
      )}
    </>
  );
}
